import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import { ErrorOutline } from '@mui/icons-material';

import {
  GoogleMap,
  Marker,
  StreetViewPanorama,
  useJsApiLoader,
} from '@react-google-maps/api';

import { useBookingsContext } from '../contexts/BookingsContext';
import useSession from '../hooks/useSession';
import Spinner from './Spinner';
import { StyledSideTab, StyledSideTabs } from './StyledTabs';
import {
  HomeOutlined,
  Man2Outlined,
  MyLocationOutlined,
} from '@mui/icons-material';
import { useAppMessage } from '@daily-co/daily-react';
import { useLocalSessionId } from '@daily-co/daily-react';
import { useParticipantProperty } from '@daily-co/daily-react';
import { useSessionContext } from '../contexts/SessionContext';
import { COLORS } from '../utils/colors';
import ToggleButton from './ToggleButton';

const libraries: 'places'[] = ['places'];

const styles = {
  iconTray: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
  },
  iconButton: {
    marginRight: '1rem',
    cursor: 'pointer',
    borderRadius: '.25rem',
    padding: '.25rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '2rem',
    height: '2rem',
    color: '#888',
    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
  },
  locationText: {
    margin: '.5rem',
  },
  container: {
    backgroundColor: '#fff',
    borderTop: `1px solid #ebebeb`,
    marginTop: '-1px',
  },
  containerInner: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    height: 'calc(100vh - 9.25rem)',
  },
};

const GoogleMaps = ({ viewTitle }: { viewTitle: string }) => {
  const { selectedBooking } = useBookingsContext();
  const { remoteParticipant } = useSessionContext();
  const { geocodePostcodeOSM } = useSession();
  const [center, setCenter] = useState<{ lat: number; lng: number }>();
  const [error, setError] = useState<string>('');
  const [value, setValue] = useState(0);
  const [showStreetView, setShowStreetView] = useState(false);
  const [address, setAddress] = useState<string>('');
  const [geolocation, setGeolocation] = useState<
    { lat: number; lng: number; error?: boolean } | undefined
  >();
  const [isGeolocationRequested, setIsGeolocationRequested] = useState(false);

  const [centerOn, setCenterOn] = useState<'none' | 'address' | 'geolocation'>(
    'none',
  );

  const handleChange = (newValue: number) => {
    setValue(newValue);
  };

  const localSessionId = useLocalSessionId();
  const [localParticipantId, localParticipantName] = useParticipantProperty(
    localSessionId,
    ['user_id', 'user_name'],
  );

  const sendPayload = (data: any) => {
    const payload = {
      senderId: localParticipantId,
      ...data,
    };

    if (payload.type !== 'mouse-move')
      console.log(`::::sendAppMessage type: ${payload.type}`, payload);
    sendAppMessage(payload, '*');
  };

  const sendAppMessage = useAppMessage({
    onAppMessage: ({ data, ...rest }: any) => {
      if (data?.type !== 'network-stats')
        console.log(
          `::::VirtualSession Room handleAppMessage: ${data?.type ? data.type : ''}`,
          data,
        );

      switch (data?.type) {
        case 'current-geolocation':
          setIsGeolocationRequested(false);

          if (!data.error) setCenterOn('geolocation');
          setGeolocation({
            lat: data.lat || 0,
            lng: data.lng || 0,
            error: data.error || false,
          });

          break;
      }
    },
  });

  useEffect(() => {
    setError('');

    const handler = (event: ErrorEvent) => {
      console.log('::::event handler: ', event);
      if (event.message?.includes('Google Maps JavaScript API error')) {
        console.error('Detected Maps error:', event.message);
        setError(event.message);
      }
    };

    window.addEventListener('error', handler);
    return () => window.removeEventListener('error', handler);
  }, []);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey:
      window._env_?.GOOGLE_MAPS_API_KEY ||
      process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
      '',
    libraries,
  });

  useEffect(() => {
    if (loadError) {
      console.error('Error loading Google Maps:', loadError);
      setError(loadError.message);
    }
  }, [loadError]);

  useEffect(() => {
    if (!selectedBooking) return;

    const addressParts = [
      selectedBooking.address,
      selectedBooking.city,
      selectedBooking.province,
    ].filter(Boolean);

    const _address = addressParts.join(', ');

    if (_address) {
      setAddress(_address);
      setCenterOn('address');
    }
  }, [selectedBooking]);

  useEffect(() => {
    if (!isLoaded) return;

    const geocoder = new window.google.maps.Geocoder();
    if (centerOn === 'address' && !!address) {
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results?.[0]) {
          const location = results[0].geometry.location;
          setCenter({
            lat: location.lat(),
            lng: location.lng(),
          });
        } else {
          console.error('Geocoding failed:', status);
        }
      });
    } else if (centerOn === 'geolocation' && geolocation) {
      setCenter({ ...geolocation });
    }
  }, [isLoaded, centerOn, address, geolocation]);

  const requestGeolocation = () => {
    if (isGeolocationRequested) return;

    setIsGeolocationRequested(true);
    sendPayload({
      type: 'get-geolocation',
    });
  };

  return (
    <>
      <StyledSideTabs value={value} onChange={() => handleChange}>
        <StyledSideTab value={0} label={viewTitle} />

        <Box sx={styles.iconTray}>
          <IconButton
            disabled={!address}
            onClick={() => {
              setCenterOn('address');
            }}
            sx={{
              ...styles.iconButton,
              border: `.1rem solid ${centerOn === 'address' ? 'orange' : '#d3d3d3'}`,
            }}>
            <HomeOutlined sx={{ fontSize: '1.5rem' }} />
          </IconButton>

          <IconButton
            disabled={!remoteParticipant || isGeolocationRequested}
            onClick={() => {
              requestGeolocation();
            }}
            sx={{
              ...styles.iconButton,
              border: `.1rem solid ${centerOn === 'geolocation' ? 'orange' : '#d3d3d3'}`,
            }}>
            {isGeolocationRequested ? (
              <CircularProgress size={'1rem'} sx={{ color: COLORS.PRIMARY }} />
            ) : geolocation?.error ? (
              <ErrorOutline sx={{ fontSize: '1.5rem', color: COLORS.RED }} />
            ) : (
              <MyLocationOutlined sx={{ fontSize: '1.5rem' }} />
            )}
          </IconButton>
          <IconButton
            onClick={() => setShowStreetView(!showStreetView)}
            sx={{
              ...styles.iconButton,
              border: `.1rem solid ${showStreetView ? 'orange' : '#d3d3d3'}`,
            }}>
            <Man2Outlined sx={{ fontSize: '1.5rem' }} />
          </IconButton>
        </Box>
      </StyledSideTabs>

      <Box id="google-map-container-outer" sx={styles.container}>
        <Typography sx={styles.locationText}>
          {centerOn === 'address'
            ? address
            : centerOn === 'geolocation'
              ? `latitude: ${center?.lat}, longtitude: ${center?.lng}`
              : ''}
        </Typography>
        <Box id="google-map-container-inner" sx={styles.containerInner}>
          {!isLoaded && !error ? (
            <Spinner open={true} />
          ) : !error &&
            (['address', 'geolocation'].includes(centerOn) || !!address) ? (
            <GoogleMap
              center={center}
              zoom={13}
              mapContainerStyle={{
                height: '100%',
                width: '100%',
              }}>
              {center && <Marker position={center} />}
              {center && showStreetView && (
                <StreetViewPanorama
                  options={{
                    position: center,
                    pov: { heading: 100, pitch: 0 },
                    visible: true,
                  }}
                />
              )}
            </GoogleMap>
          ) : (
            <Typography
              display={'flex'}
              justifyContent={'center'}
              alignItems={'center'}>
              {error
                ? error
                : centerOn === 'none' && !address
                  ? 'No address provided'
                  : 'No location found'}
            </Typography>
          )}
        </Box>
      </Box>
    </>
  );
};

export default GoogleMaps;
